import * as BoardConstants from "@/game/constants/boardConstants";
import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext";
import { configAreaTargetHighlight } from "@/game/utils/boardHighligts";
import { CounterDisplayType, renderCheckerStack } from "@/game/utils/checkerDisplayUtils";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { Constants, type GameState } from "@nextgammon/shared";
import { useEffect, useMemo } from "react";

import { Container, Text } from "@pixi/react";
import { Assets, Rectangle, TextStyle } from "pixi.js";

interface GameOffAreaProps {
  gameState: GameState;
  offBoardArea: [number, number, number, number];
  boardScale: number;
  checkerTexture: string;
  checkerCount: number; // This is the number of checkers that are off the board
  checkerDisplayCount: number; // This is the number of checkers that can be displayed in the off area
  displayCounter: CounterDisplayType;
  overlayInterface: GameBoardOverlayInterface | null;
  ownerPlayerColor: string;
  isTop: boolean;
  highlightForSingle: boolean;
  highlightForCombo: boolean;
  onClick: (playerColor: number) => void;
}

export function GameCheckerOffArea({
  gameState,
  offBoardArea,
  boardScale,
  checkerTexture,
  checkerCount,
  checkerDisplayCount,
  displayCounter,
  overlayInterface,
  ownerPlayerColor,
  isTop,
  highlightForSingle,
  highlightForCombo,
  onClick
}: GameOffAreaProps) {
  // Load the checker texture
  const texture = useMemo(
    () => (checkerTexture ? Assets.get(checkerTexture) : null),
    [checkerTexture]
  );

  // Load selection highlight texture for the top checker
  const selectedAreaTexture = useMemo(() => Assets.get("highlight-off-area"), []);

  if (!gameState.off) return null;

  const highlightOffset = boardScale * BoardConstants.POINT_HIGHLIGHT_OFFSET;
  const offIndex =
    ownerPlayerColor === Constants.PLAYER_WHITE
      ? Constants.OFF_INDEX_WHITE
      : Constants.OFF_INDEX_BLACK;
  const countLabel = checkerCount ? `x${checkerCount.toString()}` : "";
  const countLabelSize = 68 * boardScale;
  const clickRegion = offBoardArea
    ? new Rectangle(offBoardArea[0], offBoardArea[1], offBoardArea[2], offBoardArea[3])
    : null;

  // Show or hide the target state sprite
  useEffect(() => {
    if (!overlayInterface || !selectedAreaTexture || !offBoardArea) return;

    const key = `target-off-area-${offIndex}`;
    // TODO - differentiate between single and combo highlights
    if (highlightForSingle || highlightForCombo) {
      const position = {
        x: offBoardArea[0] + offBoardArea[2] / 2,
        y: offBoardArea[1] + (isTop ? offBoardArea[3] + highlightOffset : -highlightOffset)
      };
      const highlight = configAreaTargetHighlight(
        selectedAreaTexture,
        position,
        isTop ? Math.PI : 0,
        boardScale
      );

      GameLogger.logDebug(
        "GameCheckerOffArea",
        `Adding highlight for off area: ${ownerPlayerColor}`
      );

      overlayInterface.addSpriteWithTween(key, highlight.sprite, highlight.tween, true);
    } else {
      GameLogger.logDebug(
        "GameCheckerOffArea",
        `Removing highlight for off area: ${ownerPlayerColor}`
      );

      overlayInterface.removeSprite(key);
    }
    // Cleanup on unmount
    return () => {
      overlayInterface.removeSprite(key);
    };
  }, [
    highlightForSingle,
    highlightForCombo,
    offBoardArea,
    boardScale,
    selectedAreaTexture,
    overlayInterface,
    highlightOffset
  ]);

  return (
    <Container hitArea={clickRegion} eventMode="static" pointerdown={onClick}>
      {texture &&
        offBoardArea &&
        renderCheckerStack(
          texture,
          offBoardArea,
          Math.min(checkerCount, checkerDisplayCount),
          boardScale,
          boardScale,
          displayCounter === CounterDisplayType.DisplayedOnBottom
        )}
      {displayCounter !== CounterDisplayType.NotDisplayed && (
        <Text
          text={countLabel}
          x={offBoardArea[0] + offBoardArea[2] / 2}
          y={
            offBoardArea[1] +
            (displayCounter === CounterDisplayType.DisplayedOnBottom ? offBoardArea[3] : 0)
          }
          anchor={[0.5, displayCounter === CounterDisplayType.DisplayedOnBottom ? 1 : 0]}
          style={
            new TextStyle({
              fontFamily: "Arial",
              fontSize: countLabelSize,
              fill: 0xffffff,
              stroke: 0x000000,
              strokeThickness: 2
            })
          }
        />
      )}
    </Container>
  );
}
